import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Star, Search, Trophy, Plus } from 'lucide-react';
import { useFacilityData } from '../hooks/booking/useBookingContextSlices';
import { Badge, Button, Card, EmptyState, Input, InfoBanner } from '../components/ui';

const hasCoords = (g) => {
  const lat = g.latitude ?? g.lat ?? g.gps_lat;
  const lng = g.longitude ?? g.lng ?? g.gps_lng;
  return lat !== undefined && lat !== null && lat !== '' &&
         lng !== undefined && lng !== null && lng !== '';
};

const Grounds = () => {
  const { grounds } = useFacilityData();
  const [searchTerm, setSearchTerm] = useState('');

  const filteredGrounds = grounds.filter(ground => {
    const matchesSearch = ground.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (ground.location || '').toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const missingCoords = grounds.length > 0 && grounds.some((g) => !hasCoords(g));

  return (
    <div className="space-y-8 pt-16">
      {/* Header */}
      <div className="text-center">
        <div className="flex items-center justify-center mb-4">
          <Trophy className="h-10 w-10 text-ga-primary mr-3" />
          <h1 className="text-4xl font-bold text-ga-text-primary">GroundAale Services</h1>
        </div>
        <p className="text-xl text-ga-text-muted">
          Compare cricket matches, practice nets, and open nets across every venue
        </p>
        <div className="mt-4 flex justify-center">
          <Button as={Link} to="/dashboard/facility-services">
            <Plus className="h-5 w-5" />
            Add ground
          </Button>
        </div>
      </div>

      {/* Missing GPS coordinates nudge */}
      {missingCoords && (
        <InfoBanner variant="info" title="Some grounds are missing GPS coordinates">
          Set each ground's location so weather forecasts and the selfie geofence check-in work
          correctly for your players.{' '}
          <Link to="/dashboard/facility-location" className="font-medium text-ga-primary hover:underline">
            Set facility location
          </Link>
        </InfoBanner>
      )}

      {/* Search and Filter */}
      <Card>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ga-text-subtle h-5 w-5" />
            <Input
              type="text"
              placeholder="Search services by name or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
      </Card>

      {/* Results Count */}
      <div className="flex justify-between items-center">
        <p className="text-ga-text-muted">
          {filteredGrounds.length} ground{filteredGrounds.length !== 1 ? 's' : ''} found
        </p>
      </div>

      {/* Grounds Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredGrounds.map((ground) => (
          <Card key={ground.id} className="hover:shadow-lg transition-all duration-300 group">
            <div className="relative">
              <img
                src={ground.image}
                alt={ground.name}
                className="w-full h-48 object-cover rounded-ga-md mb-4 group-hover:scale-105 transition-transform duration-300"
              />
            </div>

            <h3 className="text-xl font-semibold text-ga-text-primary mb-2">{ground.name}</h3>

            <p className="text-ga-text-muted mb-3 flex items-center">
              <MapPin className="h-4 w-4 mr-1 flex-shrink-0" />
              {ground.location}
            </p>

            {/* Facilities */}
            <div className="mb-4">
              <h4 className="font-medium text-ga-text-primary mb-2">Facilities:</h4>
              <div className="flex flex-wrap gap-1">
                {(ground.facilities || []).slice(0, 3).map((facility, index) => (
                  <Badge key={index} variant="neutral">
                    {facility}
                  </Badge>
                ))}
                {(ground.facilities || []).length > 3 && (
                  <Badge variant="neutral">
                    +{ground.facilities.length - 3} more
                  </Badge>
                )}
              </div>
            </div>

            {/* Services */}
            <div className="mb-4">
              <h4 className="font-medium text-ga-text-primary mb-2">Available Services:</h4>
              <div className="space-y-2">
                {(ground.services || []).map((service) => {
                  // Determine route based on service type
                  const isTeamSports = service.type === 'cricket_match';
                  const bookingRoute = isTeamSports 
                    ? `/booking/${ground.id}/${service.id}`
                    : `/booking-new/${ground.id}/${service.id}`;
                  
                  return (
                    <Link
                      key={service.id}
                      to={bookingRoute}
                      className="block bg-ga-primary-soft hover:bg-ga-primary/15 text-ga-primary px-3 py-2 rounded-ga-sm text-sm font-medium transition-colors"
                    >
                      {service.name}
                    </Link>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <Star className="h-4 w-4 text-ga-warning fill-current" />
                <span className="text-sm text-ga-text-muted ml-1">4.8 (120 reviews)</span>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* No Results */}
      {filteredGrounds.length === 0 && (
        <EmptyState
          card
          icon={Search}
          title="No services found"
          description="Try adjusting your search criteria or filters"
          action={(
            <Button variant="secondary" onClick={() => setSearchTerm('')}>
              Clear Filters
            </Button>
          )}
        />
      )}
    </div>
  );
};

export default Grounds;
